import React, { useState } from 'react';
import { Button, Form, Card, Table, Row, Col, Container } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './Rapports.css';

const DeclarationEmployeur = () => {
  const [annexes, setAnnexes] = useState({});
  const [selectedAnnexe, setSelectedAnnexe] = useState('');
  const [company, setCompany] = useState({
    mf: '11551269BM000',
    exercice: '2025',
  });

  // -----------------------------
  // FORMAT FIXE (Format Tunisien)
  // -----------------------------
  const fix = (value, length, align = 'left') => {
    value = value ? value.toString() : '';

    // Supprimer accents et caractères spéciaux
    value = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/–/g, '-')
      .replace(/[^\x20-\x7E]/g, '');

    if (value.length > length) {
      return value.substring(0, length);
    }

    if (align === 'right') {
      return value.padStart(length, '0');
    }

    return value.padEnd(length, ' ');
  };

  // -----------------------------
  // IMPORT EXCEL (Multi-Annexes)
  // -----------------------------
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, {
        type: 'binary',
      });

      const result = {};

      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet);
        result[sheetName] = json;
      });

      setAnnexes(result);
      if (workbook.SheetNames.length > 0) {
        setSelectedAnnexe(workbook.SheetNames[0]);
      }
    };

    reader.readAsBinaryString(file);
  };

  // -----------------------------
  // MODIFICATION DES DONNÉES
  // -----------------------------
  const handleDataChange = (index, field, value) => {
    const updatedAnnexeData = [...annexes[selectedAnnexe]];
    updatedAnnexeData[index][field] = value;
    setAnnexes({
      ...annexes,
      [selectedAnnexe]: updatedAnnexeData,
    });
  };

  // -----------------------------
  // GENERATION DU TXT
  // -----------------------------
  const handleGenerate = () => {
    if (!selectedAnnexe || !annexes[selectedAnnexe]) return;

    const employees = annexes[selectedAnnexe];
    let txt = '';

    // HEADER
    txt += 'E';
    txt += fix(company.mf, 15);
    txt += fix(company.exercice, 4);
    // On peut formater le nom de l'annexe ou utiliser le nom de la feuille
    txt += fix(selectedAnnexe, 10);
    txt += '\n';

    // LIGNES EMPLOYES
    employees.forEach((emp, index) => {
      let line = 'L';
      line += fix(company.mf, 15);
      line += fix(company.exercice, 4);
      line += fix(index + 1, 6, 'right');
      line += fix(emp.CIN, 13);
      line += fix(emp.NOM_PRENOM?.toUpperCase(), 40);
      line += fix(emp.FONCTION?.toUpperCase(), 40);
      line += fix(emp.ADRESSE?.toUpperCase(), 120);

      // A112 : 1, 2, 3 ou 4
      line += fix(emp.TYPE || 1, 1);

      line += fix(emp.DATE_DEBUT || '01012025', 8);
      line += fix(emp.DATE_FIN || '31122025', 8);

      // Salaire brut (multiplier par 100 pour enlever la virgule, format fiscal)
      line += fix(
        Math.round((emp.SALAIRE_BRUT || 0) * 100),
        15,
        'right'
      );

      txt += line + '\n';
    });

    // FOOTER
    txt += 'T';
    txt += fix(company.mf, 15);
    txt += fix(company.exercice, 4);
    txt += fix(employees.length, 10, 'right');

    const blob = new Blob([txt], {
      type: 'text/plain;charset=utf-8',
    });

    saveAs(blob, `${selectedAnnexe}_${company.exercice}.txt`);
  };

  return (
    <Container className="list-rapports">
      <div className="section-header">
        <h3>Générateur Déclaration Employeur</h3>
      </div>

      <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: '24px' }}>
        <Card.Body className="p-4">
          <Row className="g-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small text-uppercase">Importation Excel (Toutes Annexes)</Form.Label>
                <div className="p-3 border-2 border-dashed rounded-4 text-center bg-light" style={{ border: '2px dashed #dee2e6' }}>
                  <Form.Control 
                    type="file" 
                    accept=".xls,.xlsx"
                    onChange={handleFile}
                    className="mx-auto"
                    style={{ maxWidth: '300px' }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small text-uppercase">Exercice</Form.Label>
                <Form.Control 
                  type="text" 
                  value={company.exercice}
                  onChange={(e) => setCompany({...company, exercice: e.target.value})}
                  className="rapport-input"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold text-muted small text-uppercase">Matricule Fiscal</Form.Label>
                <Form.Control 
                  type="text" 
                  value={company.mf}
                  onChange={(e) => setCompany({...company, mf: e.target.value})}
                  className="rapport-input"
                />
              </Form.Group>
            </Col>
          </Row>

          {Object.keys(annexes).length > 0 && (
            <Row className="mt-4 align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-muted small text-uppercase">Choisir l'Annexe à éditer/générer</Form.Label>
                  <Form.Select 
                    value={selectedAnnexe} 
                    onChange={(e) => setSelectedAnnexe(e.target.value)}
                    className="rapport-select"
                  >
                    {Object.keys(annexes).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Button 
                  onClick={handleGenerate}
                  className="rapport-button w-100 mt-0"
                  style={{ maxWidth: 'none' }}
                >
                  📥 Télécharger le fichier TXT ({selectedAnnexe})
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {selectedAnnexe && annexes[selectedAnnexe] && (
        <Card className="shadow-sm border-0 overflow-hidden" style={{ borderRadius: '24px' }}>
          <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-bold">Données de l'annexe : {selectedAnnexe}</h5>
            <span className="badge bg-soft-primary text-primary px-3 py-2 rounded-pill" style={{ backgroundColor: '#eef2ff' }}>
              {annexes[selectedAnnexe].length} enregistrements
            </span>
          </Card.Header>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="border-0 px-4">CIN / Matricule</th>
                  <th className="border-0">Nom & Prénom</th>
                  <th className="border-0">Fonction / Adresse</th>
                  <th className="border-0 text-center">Type (A112)</th>
                  <th className="border-0 px-4 text-end">Salaire Brut (DT)</th>
                </tr>
              </thead>
              <tbody>
                {annexes[selectedAnnexe].map((emp, index) => (
                  <tr key={index}>
                    <td className="px-4" style={{ width: '180px' }}>
                      <Form.Control 
                        size="sm"
                        defaultValue={emp.CIN} 
                        onBlur={(e) => handleDataChange(index, 'CIN', e.target.value)}
                        className="border-0 bg-transparent fw-bold"
                      />
                    </td>
                    <td>
                      <Form.Control 
                        size="sm"
                        defaultValue={emp.NOM_PRENOM} 
                        onBlur={(e) => handleDataChange(index, 'NOM_PRENOM', e.target.value)}
                        className="border-0 bg-transparent"
                      />
                    </td>
                    <td>
                      <Form.Control 
                        size="sm"
                        defaultValue={emp.FONCTION || emp.ADRESSE} 
                        onBlur={(e) => handleDataChange(index, emp.FONCTION ? 'FONCTION' : 'ADRESSE', e.target.value)}
                        className="border-0 bg-transparent text-muted small"
                        placeholder="Fonction ou Adresse"
                      />
                    </td>
                    <td className="text-center" style={{ width: '100px' }}>
                      <Form.Select 
                        size="sm"
                        defaultValue={emp.TYPE || 1}
                        onChange={(e) => handleDataChange(index, 'TYPE', e.target.value)}
                        className="border-0 bg-soft-primary rounded-pill px-2 text-center"
                        style={{ backgroundColor: '#eef2ff', color: '#4f46e5' }}
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </Form.Select>
                    </td>
                    <td className="px-4" style={{ width: '150px' }}>
                      <Form.Control 
                        size="sm"
                        type="number"
                        step="0.001"
                        defaultValue={emp.SALAIRE_BRUT} 
                        onBlur={(e) => handleDataChange(index, 'SALAIRE_BRUT', parseFloat(e.target.value))}
                        className="border-0 bg-transparent text-success fw-bold text-end"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      )}
    </Container>
  );
};

export default DeclarationEmployeur;
